import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { IQuickTrendAlarmViewModel, IQuickTrendMemoViewModel, IQuickTrendObjectViewModel, IQuickTrendTagViewModel, IQuickTrendViewModel } from "../../services/trending-data-service/Models";
import VirtualizedList from "../virtualized-list/VirtualizedList";
import Graph from "../graph/Graph";
import { isMobile, isTablet } from "react-device-detect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faStickyNote, IconDefinition } from "@elynx/free-solid-svg-icons";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import GraphUtils from "../../common/utilities/GraphUtils";
import { useTranslation } from "react-i18next";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";

export type GraphReportProps = {
    defaultShowAxis?: boolean
    data: IQuickTrendViewModel | undefined;
};

function GraphReport({
    defaultShowAxis,
    data
}: GraphReportProps) {

    const isPhone = isMobile && !isTablet;

    const theme = useTheme();
    const { t } = useTranslation();

    const [reportData, setReportData] = useState<IQuickTrendViewModel | undefined>(undefined);

    useEffect(() => {

        // Set some items that are necessary for the React UI.
        ((data?.quickTrendObjects?.length ?? 0) > 0) && data?.quickTrendObjects.forEach((opt, index) => {

            opt.chartOptions.headerTitle = opt.objectDisplayName;
            opt.chartOptions.index = index;
            opt.chartOptions.id = GraphUtils.getChartID();

            // We must set these to undefined to allow Flex to work correctly. The server is 
            //  setting a height and width and we don't want it set.
            if (opt.chartOptions.chart) {
                opt.chartOptions.chart.marginTop = 15;
                opt.chartOptions.chart.height = undefined;
                opt.chartOptions.chart.width = undefined;
            }
        });

        // Test
        //if (data?.quickTrendObjects) {
        //    data.quickTrendObjects = data.quickTrendObjects.slice(2, 3);
        //}

        setReportData(data);
    }, [
        data,
        setReportData
    ]);

    function getAvatar(item: IQuickTrendAlarmViewModel) {
        return (
            <Box sx={{
                position: "relative",
                width: (theme) => theme.spacing(4)
            }}
            >
                <FontAwesomeIcon icon={getIcon(item)} size="2x" color={getStatusColor(item)} />
            </Box>
        );
    }

    function getIcon(item: IQuickTrendAlarmViewModel): IconDefinition {
        //return item.disabled ? faBellSlash : faBell;
        return faBell;
    }

    function getStatusColor(item: IQuickTrendAlarmViewModel): string | undefined {

        // We have no current way to get the configured Alarm Setpoint colors
        return item.isAcknowledged
            ? "green"
            : item.alarmStatus
                ? "red"
                : undefined;
    }

    function getCurrentStateText(item: IQuickTrendAlarmViewModel): string {
        return item.alarmStatus
            ? item.isAcknowledged
                ? t("enums.alarmNotificationEndingReason.acknowledged")
                : t("alarm")
            : t("enums.alarmNotificationEndingReason.clear")
    }

    function renderAlarms(alarms: Array<IQuickTrendTagViewModel>) {

        return (
            <Typography component="div" gutterBottom variant="inherit"
                sx={{
                    marginLeft: 2
                }}>
                {alarms.map((opt, index) =>
                    <React.Fragment key={index}>

                        <Typography variant="subtitle1" sx={{ marginBottom: theme.spacing(.5), width: '100%' }}>
                            {opt.displayName}
                        </Typography>

                        <Stack direction='column'>
                            {opt.alarms.map((opt, index) =>

                                <React.Fragment key={index}>
                                    <Stack direction={{
                                        xs: "column", sm: "row"
                                    }} sx={{ marginBottom: theme.spacing(.5) }}>
                                        {getAvatar(opt)}

                                        <Box sx={{
                                            color: getStatusColor(opt)
                                        }} >
                                            {getCurrentStateText(opt)}
                                        </Box>
                                        <Box sx={{ marginLeft: theme.spacing(1) }}>
                                            <Typography component="span" noWrap variant="inherit">
                                                {opt.eventDateTime ? format(new Date(opt.eventDateTime), "P pp") : ""}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ marginLeft: theme.spacing(1) }}>
                                            {opt.setPointName}
                                        </Box>
                                        <Box sx={{ marginLeft: theme.spacing(1) }}>
                                            Value ({opt.value})
                                        </Box>
                                    </Stack>

                                </React.Fragment>
                            )}
                        </Stack>
                    </React.Fragment>
                )}
            </Typography>
        );
    }

    function renderMemos(memos: Array<IQuickTrendMemoViewModel>) {

        return (
            <Typography component="div" variant="inherit"
                sx={{
                    marginLeft: 2
                }}>

                <Stack direction='column'>
                    {memos.map((opt, index) =>

                        <React.Fragment key={index}>
                            <Stack direction={{
                                xs: "column", sm: "row"
                            }} sx={{ marginBottom: theme.spacing(.5) }} >
                                <FontAwesomeIcon icon={faStickyNote} size="2x" />

                                <Box sx={{ marginLeft: theme.spacing(1) }}>
                                    <Typography component="span" noWrap variant="inherit">
                                        {opt.dateTime ? format(new Date(opt.dateTime), "P pp") : ""}
                                    </Typography>
                                </Box>
                                <Box sx={{ marginLeft: theme.spacing(1) }}>
                                    {opt.text}
                                </Box>
                                {opt.hasUserInfo &&
                                    <Box sx={{ marginLeft: theme.spacing(1) }}>
                                        {opt.userInfo}
                                    </Box>
                                }
                            </Stack>
                        </React.Fragment>
                    )}
                </Stack>
            </Typography>
        );
    }

    function renderItem(_index: number, item: IQuickTrendObjectViewModel) {
        return (
            <>
                <Box sx={{
                    padding: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: `1 1 ${isPhone ? '100%' : '50%'}`,
                    '& + &': {
                        marginTop: 1
                    },
                    minHeight: isPhone ? 300 : 600
                }}>
                    <Typography
                        variant="subtitle1"
                        //noWrap
                        component="div"
                        sx={{
                            fontWeight: 700,
                            display: 'flex',
                            flex: '0 0 auto',

                            //marginBottom: 1
                            alignSelf: 'center'
                        }}>
                        {item.objectDisplayName}
                    </Typography>

                    <Graph key={_index}
                        defaultShowAxis={defaultShowAxis}
                        ignoreScrollTop={true}
                        options={
                            {
                                optionItems: [item.chartOptions],
                                listTextTagMapping: data?.listTextTagMapping
                            }}
                        showAxesToggle={true}
                        />
                </Box>
                <Typography component="div" variant="inherit"
                    sx={{
                        marginLeft: 2
                    }}>

                    {item.hasTagAlarmsDiscrete &&

                        <>
                            <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                {t("graphReport.fieldAlarms")}
                            </Typography>

                            {renderAlarms(item.tagAlarmsDiscrete)}
                        </>
                    }

                    {item.hasTagAlarmsAnalog &&

                        <>
                            <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                {t("graphReport.databaseAlarms")}
                            </Typography>

                            {renderAlarms(item.tagAlarmsAnalog)}
                        </>
                    }

                    {item.hasObjectMemos &&

                        <>
                            <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                {`${getObjectTypeName(eLynxObjectTypeEnum.Device)} ${t("memos")}`}
                            </Typography>

                            {renderMemos(item.objectMemos)}
                        </>
                    }

                    {item.hasTagMemos &&

                        <>
                            <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                {`${getObjectTypeName(eLynxObjectTypeEnum.Tag)} ${t("memos")}`}
                            </Typography>

                            <Typography component="div" variant="inherit"
                                sx={{
                                    marginLeft: 2
                                }}>
                                {item.tagMemos.map((opt, index) =>
                                    <React.Fragment key={index}>

                                        <Typography variant="subtitle1" gutterBottom sx={{
                                            fontWeight: 'bold'
                                        }}>
                                            {opt.displayName}
                                        </Typography>

                                        {renderMemos(opt.memos)}
                                    </React.Fragment>
                                )}
                            </Typography>
                        </>
                    }
                </Typography>
            </>
        );
    };

    return (
        <VirtualizedList
            items={reportData?.quickTrendObjects ?? []}
            renderItem={renderItem}
        />
    );
}

export default GraphReport;